import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [banInfo, setBanInfo] = useState(null);

  const API_BASE = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');

  // Handle banned user - force logout
  const handleBannedUser = useCallback((banData) => {
    console.log('[AUTH] User banned, forcing logout:', banData);
    setBanInfo(banData);
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('cart_id');
    // Redirect to login page with ban info
    window.location.href = '/login?banned=true';
  }, []);

  // Check API response for ban status
  const checkBanResponse = useCallback((response, data) => {
    if (response.status === 403 && data?.detail?.error === 'account_banned') {
      handleBannedUser(data.detail);
      return true;
    }
    return false;
  }, [handleBannedUser]);

  // Periodic ban status check - runs every 10 seconds when user is logged in
  useEffect(() => {
    let banCheckInterval;
    
    const checkBanStatus = async () => {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      try {
        const response = await fetch(`${API_BASE}/api/auth/ban-status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.banned) {
            console.log('[AUTH] User is banned, forcing logout');
            handleBannedUser({
              banned_until: data.banned_until,
              ban_reason: data.ban_reason
            });
          }
        } else if (response.status === 403) {
          // User might be banned - check response
          try {
            const data = await response.json();
            if (data?.detail?.error === 'account_banned') {
              handleBannedUser(data.detail);
            }
          } catch (e) {
            // Not a ban response
          }
        }
      } catch (error) {
        console.error('[AUTH] Ban status check failed:', error);
      }
    };
    
    // Only start polling if user is logged in
    if (user) {
      // Check immediately on login
      checkBanStatus();
      // Then check every 10 seconds
      banCheckInterval = setInterval(checkBanStatus, 10000);
    }
    
    return () => {
      if (banCheckInterval) {
        clearInterval(banCheckInterval);
      }
    };
  }, [user, API_BASE, handleBannedUser]);

  useEffect(() => {
    // Check if user is logged in from localStorage
    const storedUser = localStorage.getItem('user');
    const storedToken = localStorage.getItem('token');
    
    if (storedUser && storedToken) {
      try {
        setUser(JSON.parse(storedUser));
      } catch (error) {
        console.error('Error parsing stored user:', error);
        localStorage.removeItem('user');
        localStorage.removeItem('token');
      }
    }
    setLoading(false);
  }, []);

  const login = async (email, password) => {
    try {
      const response = await fetch(`${API_BASE}/api/auth/login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();
      
      console.log('[AUTH] Login response status:', response.status);
      console.log('[AUTH] Login response data:', data);

      if (response.ok && data.success) {
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token);
        return { success: true, user: data.user };
      } else {
        // Check for banned user (403 status with USER_BANNED message)
        if (data.message === 'USER_BANNED') {
          console.log('[AUTH] User is banned, returning ban info');
          return {
            success: false,
            message: 'USER_BANNED',
            banned_until: data.banned_until,
            ban_reason: data.ban_reason
          };
        }
        
        return { success: false, message: data.detail || 'Invalid email or password' };
      }
    } catch (error) {
      console.error('Login error:', error);
      return { success: false, message: 'Login failed. Please try again.' };
    }
  };

  const register = async (userData) => {
    try {
      console.log('[AUTH] Registering user at:', `${API_BASE}/api/auth/register`);
      console.log('[AUTH] User data keys:', Object.keys(userData));
      console.log('[AUTH] Role:', userData.role);
      
      const response = await fetch(`${API_BASE}/api/auth/register`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      });

      console.log('[AUTH] Response status:', response.status);
      console.log('[AUTH] Response ok:', response.ok);
      
      // Read response as text first, then parse
      let responseText;
      let data;
      
      try {
        responseText = await response.text();
        console.log('[AUTH] Response text:', responseText);
        
        // Try to parse as JSON
        data = JSON.parse(responseText);
        console.log('[AUTH] Response data:', data);
      } catch (parseError) {
        console.error('[AUTH] Failed to parse response:', parseError);
        console.error('[AUTH] Raw response:', responseText);
        return { 
          success: false, 
          message: `Server error: ${responseText ? responseText.substring(0, 200) : 'Empty response'}` 
        };
      }

      if (response.ok && data.success) {
        console.log('[AUTH] Registration successful!');
        // For trainers, return token but don't auto-login (pending approval)
        if (userData.role === 'trainer') {
          return { 
            success: true, 
            message: data.message || 'Registration successful. Awaiting admin approval.',
            token: data.token  // Return token for file uploads
          };
        }
        
        // For users, auto-login
        setUser(data.user);
        localStorage.setItem('user', JSON.stringify(data.user));
        localStorage.setItem('token', data.token);
        return { success: true, user: data.user };
      } else {
        console.error('[AUTH] Registration failed:', data);
        return { 
          success: false, 
          message: data.detail || data.message || JSON.stringify(data) || 'Registration failed' 
        };
      }
    } catch (error) {
      console.error('[AUTH] Registration error:', error);
      console.error('[AUTH] Error stack:', error.stack);
      return { 
        success: false, 
        message: `Registration failed: ${error.message}. Please check your internet connection.` 
      };
    }
  };

  const logout = () => {
    setUser(null);
    localStorage.removeItem('user');
    localStorage.removeItem('token');
    localStorage.removeItem('cart_id'); // Also clear cart
  };

  // Function to update user info and sync with localStorage
  const updateUser = (updatedUserData) => {
    console.log('[AuthContext] updateUser called with:', updatedUserData);
    console.log('[AuthContext] Current user:', user);
    const newUser = { ...user, ...updatedUserData };
    console.log('[AuthContext] New user:', newUser);
    setUser(newUser);
    localStorage.setItem('user', JSON.stringify(newUser));
    console.log('[AuthContext] User updated and saved to localStorage');
  };

  // Update user's timezone/city
  const updateTimezone = async (timezoneData) => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE}/api/users/update-timezone`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(timezoneData),
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        // Update local user state
        updateUser({
          city: timezoneData.city,
          province: timezoneData.province,
          timezone: timezoneData.timezone,
        });
        return { success: true };
      } else {
        return { success: false, message: data.detail || 'Failed to update timezone' };
      }
    } catch (error) {
      console.error('Error updating timezone:', error);
      return { success: false, message: 'Failed to update timezone' };
    }
  };

  // Check if user needs to set timezone
  const needsTimezoneSetup = user && !user.timezone && user.role !== 'admin';

  // Clear ban info
  const clearBanInfo = () => setBanInfo(null);

  const value = {
    user,
    setUser: updateUser,
    login,
    register,
    logout,
    loading,
    isAuthenticated: !!user,
    isAdmin: user?.role === 'admin',
    isTrainer: user?.role === 'trainer',
    isUser: user?.role === 'user',
    updateTimezone,
    needsTimezoneSetup,
    banInfo,
    clearBanInfo,
    checkBanResponse,
    handleBannedUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};